import {
  MAX_SPECIAL_INSTRUCTIONS,
  type DraftPurchaseOrderSubmission,
  type DraftSubmissionChoice,
  type SetDraftSubmissionInput,
  type SubmissionDefaults,
} from '@erp/rpc';

/** The sending fields of a draft, or of a PO about to be released. */
type SubmissionValues = Pick<
  SubmissionDefaults,
  | 'sendByEdi'
  | 'emailTo'
  | 'emailCc'
  | 'emailSubject'
  | 'creditTermsCode'
  | 'specialInstructions'
>;

/** "No code" in the usage code picker. */
export const NO_CODE = 'none';

export interface DraftSubmissionForm {
  sendByEdi: boolean;
  emailTo: string;
  emailCc: string;
  emailSubject: string;
  creditTermsCode: string;
  /** Always MAX_SPECIAL_INSTRUCTIONS rows; blank rows are dropped on save. */
  instructions: { slot: string; text: string; code: string }[];
}

export function formFromSubmission(
  submission: SubmissionValues,
): DraftSubmissionForm {
  const rows = Array.from({ length: MAX_SPECIAL_INSTRUCTIONS }, (_, index) => {
    const line = submission.specialInstructions[index];
    return {
      slot: String(index + 1),
      text: line?.text ?? '',
      code: line?.code ?? NO_CODE,
    };
  });
  return {
    sendByEdi: submission.sendByEdi ?? false,
    emailTo: submission.emailTo ?? '',
    emailCc: submission.emailCc ?? '',
    emailSubject: submission.emailSubject ?? '',
    creditTermsCode: submission.creditTermsCode ?? '',
    instructions: rows,
  };
}

/**
 * The form after the EDI box changes. Switching EDI off with no recipient
 * fills in the vendor's PO email, so the PO still reaches the vendor.
 */
export function withSendByEdi(
  form: DraftSubmissionForm,
  sendByEdi: boolean,
  vendorEmail: string | null,
): DraftSubmissionForm {
  const noRecipient = !sendByEdi && !form.emailTo.trim();
  return {
    ...form,
    sendByEdi,
    emailTo: noRecipient && vendorEmail ? vendorEmail : form.emailTo,
  };
}

const blankToNull = (value: string) => value.trim() || null;

/**
 * The vendor's PO email to offer when the PO goes by email to someone else,
 * e.g. the buyer an EDI PO was copied to before EDI was switched off.
 */
export function vendorEmailToOffer(
  form: Pick<DraftSubmissionForm, 'sendByEdi' | 'emailTo'>,
  vendorEmail: string | null,
): string | null {
  if (form.sendByEdi || !vendorEmail) return null;
  return form.emailTo.trim().toLowerCase() === vendorEmail.toLowerCase()
    ? null
    : vendorEmail;
}

/** What saving sends: EDI only when it changed, blanks as empty. */
export function submissionSaveInput(
  submission: DraftPurchaseOrderSubmission,
  form: DraftSubmissionForm,
): SetDraftSubmissionInput {
  return {
    draftPurchaseOrderId: submission.draftPurchaseOrderId,
    ...changedSubmissionFields(submission, form),
  };
}

/**
 * The fields the buyer changed from what the form loaded, blanks as empty.
 * Releasing sends them over the vendor's defaults (or an open PO's own).
 */
export function changedSubmissionFields(
  loadedSubmission: SubmissionValues,
  form: DraftSubmissionForm,
): DraftSubmissionChoice {
  const next = {
    sendByEdi: form.sendByEdi,
    emailTo: blankToNull(form.emailTo),
    emailCc: blankToNull(form.emailCc),
    emailSubject: blankToNull(form.emailSubject),
    creditTermsCode: blankToNull(form.creditTermsCode),
    specialInstructions: form.instructions.flatMap((row) =>
      row.text.trim()
        ? [
            {
              text: row.text.trim(),
              code: row.code === NO_CODE ? null : row.code,
            },
          ]
        : [],
    ),
  };
  const loaded: Record<keyof typeof next, unknown> = {
    sendByEdi: loadedSubmission.sendByEdi ?? false,
    emailTo: loadedSubmission.emailTo,
    emailCc: loadedSubmission.emailCc,
    emailSubject: loadedSubmission.emailSubject,
    creditTermsCode: loadedSubmission.creditTermsCode,
    specialInstructions: loadedSubmission.specialInstructions,
  };
  // Only what this buyer changed, so saving never writes back values someone
  // else changed after the form loaded.
  const changed = Object.fromEntries(
    Object.entries(next).filter(
      ([key, value]) =>
        JSON.stringify(value) !==
        JSON.stringify(loaded[key as keyof typeof next]),
    ),
  ) as Partial<typeof next>;
  return changed;
}

/** "A · 2% 10 NET 30", or the bare code without a description. */
export function creditTermsLabel(
  code: string | null,
  description: string | null,
): string {
  if (!code) return '';
  return description ? `${code} · ${description}` : code;
}
